import { Text } from "@react-three/drei";

/**
 * Reusable diegetic-text plaque mounted on a wall (or wherever).
 *
 * Backer is a primitive box for this pass — swap to a GLB mesh (sign-wood,
 * tablet-stone, board-bulletin) by replacing the <mesh> below with
 * <primitive object={useGLTF(url).scene.clone()} /> once the Kenney Castle Kit
 * assets land in client/public/models/castle-kit/.
 *
 * Default orientation: faces +z (so a plaque on the back wall at -z gets no
 * rotation; flip rotY by Math.PI for a plaque on the front wall, etc.).
 *
 * Text is rendered slightly in front of the backer face (offset on +z) so it
 * doesn't z-fight with the mesh. Uses drei <Text> (SDF font) for crisp text
 * at any camera distance.
 */

const TEXT_OFFSET = 0.03;

export interface PlaqueProps {
  position: [number, number, number];
  rotation?: [number, number, number];
  width: number;
  height: number;
  depth?: number;
  backerColor?: string;
  title?: string;
  titleColor?: string;
  titleSize?: number;
  lines: string[];
  bodyColor?: string;
  bodySize?: number;
  lineSpacing?: number;
  align?: "left" | "center";
  maxWidth?: number;
}

export function Plaque({
  position,
  rotation = [0, 0, 0],
  width,
  height,
  depth = 0.12,
  backerColor = "#1a1208",
  title,
  titleColor = "#ffd66b",
  titleSize = 0.38,
  lines,
  bodyColor = "#f4ecd8",
  bodySize = 0.30,
  lineSpacing = 1.4,
  align = "center",
  maxWidth,
}: PlaqueProps): JSX.Element {
  const anchorX = align === "center" ? "center" : "left";
  const textX = align === "center" ? 0 : -width / 2 + 0.2;
  const titleY = title ? height / 2 - titleSize - 0.1 : height / 2 - 0.1;
  const firstLineY = title ? titleY - titleSize * 1.5 : titleY;

  // Auto-fit body text vertically: if N lines at the requested bodySize would
  // overflow the available space, scale bodySize + lineStep down uniformly so
  // the last line lands inside the backer. Boards that already fit are
  // untouched (fitScale = 1). Boards with too much copy get smaller text
  // rather than spilling off the bottom edge.
  const naturalLineStep = bodySize * lineSpacing;
  const naturalBodySpan =
    lines.length === 0 ? 0 : (lines.length - 1) * naturalLineStep + bodySize;
  const bottomMargin = 0.15;
  const availableBodySpan = firstLineY + height / 2 - bottomMargin;
  const fitScale =
    naturalBodySpan > availableBodySpan && naturalBodySpan > 0
      ? availableBodySpan / naturalBodySpan
      : 1;
  const effectiveBodySize = bodySize * fitScale;
  const effectiveLineStep = naturalLineStep * fitScale;
  const effectiveMaxWidth = maxWidth ?? width - 0.3;

  return (
    <group position={position} rotation={rotation}>
      {/* Outer gold trim frame (slightly larger than the backer) */}
      <mesh position={[0, 0, -depth / 2 - 0.005]}>
        <boxGeometry args={[width + 0.16, height + 0.16, depth * 0.5]} />
        <meshStandardMaterial color="#8a6a28" roughness={0.5} metalness={0.5} />
      </mesh>
      {/* Dark backer */}
      <mesh receiveShadow castShadow>
        <boxGeometry args={[width, height, depth]} />
        <meshStandardMaterial color={backerColor} roughness={0.9} metalness={0.05} />
      </mesh>

      {/* Title */}
      {title && (
        <Text
          position={[textX, titleY, depth / 2 + TEXT_OFFSET]}
          fontSize={titleSize}
          color={titleColor}
          anchorX={anchorX}
          anchorY="top"
          maxWidth={effectiveMaxWidth}
          outlineWidth={0.02}
          outlineColor="#0a0a10"
          letterSpacing={0.04}
        >
          {title}
        </Text>
      )}

      {/* Body lines */}
      {lines.map((line, i) => (
        <Text
          key={i}
          position={[textX, firstLineY - i * effectiveLineStep, depth / 2 + TEXT_OFFSET]}
          fontSize={effectiveBodySize}
          color={bodyColor}
          anchorX={anchorX}
          anchorY="top"
          maxWidth={effectiveMaxWidth}
          outlineWidth={0.016 * fitScale}
          outlineColor="#0a0a10"
          letterSpacing={0.02}
        >
          {line}
        </Text>
      ))}
    </group>
  );
}
